// ─────────────────────────────────────────────
//  WeightResult.jsx — Resultado visual + báscula
// ─────────────────────────────────────────────

import React, { useMemo } from 'react';
import styles from './WeightResult.module.css';
import { formatCurrency } from '../../utils/format';

function formatGrams(grams) {
	return Math.round(grams).toLocaleString('es-MX');
}

export default function WeightResult({
	weightGrams,
	budget,
	hasReference,
	pricePerGram,
	weightMin,
	weightMax,
}) {
	const grams = Math.round(weightGrams);
	const kg = grams / 1000;

	const sliceScale = useMemo(() => {
		const t = (grams - weightMin) / (weightMax - weightMin);
		return 0.55 + t * 0.85;
	}, [grams, weightMin, weightMax]);

	const needleRotation = useMemo(() => {
		const t = (grams - weightMin) / (weightMax - weightMin);
		return -55 + t * 110;
	}, [grams, weightMin, weightMax]);

	if (!hasReference) {
		return (
			<section className={styles.card} aria-label="Resultado">
				<p className={styles.empty} role="status">
					Configura la referencia para ver cuánto te deben dar
				</p>
			</section>
		);
	}

	return (
		<section className={styles.card} aria-label="Resultado de peso">
			<div className={styles.visualWrap}>
				<div className={styles.scale} aria-hidden="true">
					<div className={styles.scaleScreen}>
						<span className={styles.scaleLabel}>BÁSCULA</span>
						<span className={styles.scaleDigits} key={grams}>
							{formatGrams(weightGrams)}
						</span>
						<span className={styles.scaleUnit}>g</span>
					</div>
					<div className={styles.scaleBase}>
						<div
							className={styles.needle}
							style={{ transform: `rotate(${needleRotation}deg)` }}
						/>
					</div>
				</div>

				<div
					className={styles.sliceWrap}
					style={{ transform: `scale(${sliceScale})` }}
					aria-hidden="true"
				>
					<span className={styles.slice}>🥓</span>
					<span className={styles.sliceShadow} />
				</div>
			</div>

			<div className={styles.resultRows}>
				<div className={styles.row}>
					<span className={styles.rowLabel}>Presupuesto</span>
					<span className={`${styles.rowValue} ${styles.budgetColor}`}>
						{formatCurrency(budget, { forceInteger: true })}
					</span>
				</div>

				<div className={styles.row}>
					<span className={styles.rowLabel}>Precio por gramo</span>
					<span className={styles.rowValue}>
						{formatCurrency(pricePerGram)}
					</span>
				</div>

				<div className={styles.divider} />

				<div className={`${styles.row} ${styles.totalRow}`}>
					<span className={styles.totalLabel}>Te deben dar</span>
					<span className={`${styles.totalValue} ${styles.weightColor}`} aria-live="polite">
						{formatGrams(weightGrams)}
						<span className={styles.totalUnit}> g</span>
					</span>
				</div>

				<p className={styles.kgHint} aria-live="polite">
					≈ {kg >= 1 ? `${kg.toFixed(2)} kg` : `${kg.toFixed(3)} kg`}
				</p>
			</div>
		</section>
	);
}
